<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { localizedMessageTimestamp } from 'shared/helpers/timeHelper';
import { useLocale } from 'shared/composables/useLocale';
import BaseBubble from './Base.vue';
import { useMessageContext } from '../provider.js';

const { content, contentAttributes, createdAt } = useMessageContext();
const { t } = useI18n();
const { resolvedLocale } = useLocale();

const readableTime = computed(() =>
  localizedMessageTimestamp(
    createdAt.value,
    'LLL d, h:mm a',
    resolvedLocale.value
  )
);

import { emitter } from 'shared/helpers/mitt';
const continuationRegex =
  /Esta conversa é uma continuação da Conversa #(\d+)\./;
const isContinuationMessage = computed(() => {
  return continuationRegex.test(content.value);
});
const continuationId = computed(() => {
  const match = content.value.match(continuationRegex);
  return match ? match[1] : null;
});

const activityAttributes = computed(() => contentAttributes.value || {});
const structuredOpportunityActivity = computed(() => {
  const attributes = activityAttributes.value;
  if (attributes.activity_type !== 'opportunity_created') return null;

  return {
    accountId: attributes.account_id || attributes.accountId,
    opportunityId: attributes.opportunity_id || attributes.opportunityId,
    date: attributes.date,
  };
});

// Older events were persisted as Portuguese HTML. Render them through the
// same i18n path so changing the user's language also updates historical data.
const legacyOpportunityActivity = computed(() => {
  const match = content.value?.match(
    /href=['"]([^'"]*sales-pipeline\?opportunity_id=(\d+)[^'"]*)['"][^>]*>\s*Oportunidade no Pipeline criada em\s+([^.<]+)\./
  );
  if (!match) return null;

  return { href: match[1], opportunityId: match[2], date: match[3] };
});

const legacyTicketActivity = computed(() => {
  const match = content.value?.match(
    /href=['"]([^'"]*tickets\?ticket_id=(\d+)[^'"]*)['"][^>]*>\s*Chamado nº\s+\d+ criado em\s+([^.<]+)\./
  );
  if (!match) return null;

  return { href: match[1], ticketId: match[2], date: match[3] };
});

const legacyAppointmentActivity = computed(() => {
  const match = content.value?.match(
    /href=['"]([^'"]*calendar\?appointment_id=(\d+)[^'"]*)['"][^>]*>\s*Compromisso (criado|atualizado):\s*(.+) em (\d{2}\/\d{2}\/\d{4} às \d{2}:\d{2})\./
  );
  if (!match) return null;

  return {
    href: match[1],
    appointmentId: match[2],
    action: match[3] === 'criado' ? 'created' : 'updated',
    title: match[4],
    startsAt: match[5],
  };
});

const opportunityActivity = computed(
  () => structuredOpportunityActivity.value || legacyOpportunityActivity.value
);

const opportunityActivityHref = computed(() => {
  const activity = opportunityActivity.value;
  if (!activity) return '#';
  if (activity.href) return activity.href;

  return `/app/accounts/${activity.accountId}/sales-pipeline?opportunity_id=${activity.opportunityId}`;
});

const ticketActivity = computed(() => {
  const attributes = activityAttributes.value;
  if (attributes.activity_type !== 'ticket_created') {
    return legacyTicketActivity.value;
  }

  return {
    accountId: attributes.account_id || attributes.accountId,
    ticketId: attributes.ticket_id || attributes.ticketId,
    date: attributes.date,
    href: null,
  };
});

const appointmentActivity = computed(() => {
  const attributes = activityAttributes.value;
  if (
    !['appointment_created', 'appointment_updated'].includes(
      attributes.activity_type
    )
  ) {
    return legacyAppointmentActivity.value;
  }

  return {
    accountId: attributes.account_id || attributes.accountId,
    appointmentId: attributes.appointment_id || attributes.appointmentId,
    title: attributes.title,
    startsAt: attributes.starts_at || attributes.startsAt,
    action:
      attributes.activity_type === 'appointment_created'
        ? 'created'
        : 'updated',
    href: null,
  };
});

const ticketActivityHref = computed(() => {
  if (!ticketActivity.value) return '#';
  if (ticketActivity.value.href) return ticketActivity.value.href;
  return `/app/accounts/${ticketActivity.value.accountId}/tickets?ticket_id=${ticketActivity.value.ticketId}`;
});

const appointmentActivityHref = computed(() => {
  if (!appointmentActivity.value) return '#';
  if (appointmentActivity.value.href) return appointmentActivity.value.href;
  return `/app/accounts/${appointmentActivity.value.accountId}/calendar?appointment_id=${appointmentActivity.value.appointmentId}`;
});

const appointmentActivityText = computed(() => {
  if (!appointmentActivity.value) return '';
  const values = {
    title: appointmentActivity.value.title,
    date: appointmentActivity.value.startsAt,
  };

  return appointmentActivity.value.action === 'created'
    ? t('CONVERSATION.ACTIVITY.APPOINTMENT_CREATED', values)
    : t('CONVERSATION.ACTIVITY.APPOINTMENT_UPDATED', values);
});

const openContinuationModal = () => {
  if (continuationId.value) {
    emitter.emit('openConversationModal', continuationId.value);
  }
};
</script>

<template>
  <BaseBubble
    v-tooltip.top="readableTime"
    class="px-3 py-1 !rounded-xl flex min-w-0 items-center gap-2"
    data-bubble-name="activity"
  >
    <span v-if="isContinuationMessage">
      {{ t('CONVERSATION.CONTINUATION_PREFIX') }}
      <a
        href="#"
        class="text-woot-500 font-medium hover:underline"
        @click.prevent="openContinuationModal"
        >{{ t('CONVERSATION.CONVERSATION_NUMBER', { id: continuationId }) }}</a
      >{{ t('COMMON.SENTENCE_PERIOD') }}
    </span>
    <a
      v-else-if="opportunityActivity"
      :href="opportunityActivityHref"
      class="text-woot-500 font-medium hover:underline"
    >
      {{
        t('CONVERSATION.ACTIVITY.OPPORTUNITY_CREATED', {
          date: opportunityActivity.date,
        })
      }}
    </a>
    <a
      v-else-if="ticketActivity"
      :href="ticketActivityHref"
      class="text-woot-500 font-medium hover:underline"
    >
      {{
        t('CONVERSATION.ACTIVITY.TICKET_CREATED', {
          id: ticketActivity.ticketId,
          date: ticketActivity.date,
        })
      }}
    </a>
    <a
      v-else-if="appointmentActivity"
      :href="appointmentActivityHref"
      class="text-woot-500 font-medium hover:underline"
    >
      {{ appointmentActivityText }}
    </a>
    <span v-else v-dompurify-html="content" :title="content" />
  </BaseBubble>
</template>
